import type { ReactNode } from "react";

import type { InputGroupValue } from "~/shared/components/input-group.tsx";
import { InputFieldsGroup } from "~/shared/components/input-group.tsx";
import type { InputFieldConfig } from "~/shared/types/input-group-types.ts";
import type { HandlingUnit } from "~/shared/types/quote-details-types";
import { getEstClass } from "~/shared/utils/commodity-utils.ts";

export type CommodityFormValues = Omit<
	Pick<
		HandlingUnit,
		| "id"
		| "quantity"
		| "unit"
		| "weight"
		| "length"
		| "width"
		| "height"
		| "description"
		| "class"
		| "nmfc"
		| "pieceCount"
		| "density"
		| "estClass"
		| "isStackable"
		| "isHazmat"
	>,
	"unit" | "class"
> & {
	unit: HandlingUnit["unit"] | null;
	class: HandlingUnit["class"] | null;
};

export type CommodityFormField = Exclude<
	keyof CommodityFormValues,
	"id" | "density" | "estClass"
>;

type CommodityFormProps = {
	values: CommodityFormValues;
	mode: "add" | "edit";
	onChange: (field: CommodityFormField, value: InputGroupValue) => void;
	groupId: number;
	unitTypes?: InputFieldConfig["data"];
	freightClassData?: InputFieldConfig["data"];
	isMobile: boolean;
	measuringType?: "LB/IN" | "KG/CM";
	weightLabel?: string;
	requireDescription?: boolean;
	requireClass?: boolean;
	showPieceCount?: boolean;
	requirePieceCount?: boolean;
	showCalculatedFields?: boolean;
	showCommodityFlags?: boolean;
	disabled?: boolean;
	validationErrors?: Set<string>;
};

type FieldOptions = Pick<
	CommodityFormProps,
	| "mode"
	| "isMobile"
	| "measuringType"
	| "weightLabel"
	| "requireDescription"
	| "requireClass"
	| "showPieceCount"
	| "requirePieceCount"
	| "showCalculatedFields"
	| "showCommodityFlags"
	| "unitTypes"
	| "freightClassData"
> & {
	values: CommodityFormValues;
	groupId: number;
};

const EDIT_FIELD_KEYS: Record<CommodityFormField, string> = {
	description: "description",
	quantity: "quantity",
	unit: "unitTypeId",
	weight: "weight",
	length: "dimensionLength",
	width: "dimensionWidth",
	height: "dimensionHeight",
	class: "freightClassId",
	nmfc: "nationalMotorFreightClassification",
	pieceCount: "pieceCount",
	isStackable: "isStackable",
	isHazmat: "isHazmat",
};

const ADD_FORM_FIELDS_BY_KEY: Record<string, CommodityFormField> = {
	description: "description",
	quantity: "quantity",
	unit: "unit",
	weight: "weight",
	length: "length",
	width: "width",
	height: "height",
	class: "class",
	nmfc: "nmfc",
	pieceCount: "pieceCount",
	isStackable: "isStackable",
	isHazmat: "isHazmat",
};

const EDIT_FORM_FIELDS_BY_KEY: Record<string, CommodityFormField> =
	Object.fromEntries(
		Object.entries(EDIT_FIELD_KEYS).map(([field, key]) => [key, field]),
	) as Record<string, CommodityFormField>;

const getFieldWrapperStyle = (
	minWidth: number,
): InputFieldConfig["wrapperStyle"] => ({
	flex: `1 1 ${minWidth}px`,
	minWidth,
});

const formatDecimalDisplay = (value?: number | null) => {
	if (value == null || value === 0) {
		return "—";
	}
	return (Math.floor(value * 100) / 100).toFixed(2);
};

const getDimensionSeparator = (): ReactNode => (
	<div className={"x"}>
		<div>X</div>
	</div>
);

const getDimensionAfter = (
	mode: CommodityFormProps["mode"],
	index: number,
): ReactNode | undefined => {
	if (mode !== "add" || index >= 2) {
		return undefined;
	}
	return index === 0 ? getDimensionSeparator() : <div className={"x"}>X</div>;
};

const getPresentationFieldKey = (
	field: CommodityFormField,
	mode: CommodityFormProps["mode"],
) => (mode === "add" ? field : EDIT_FIELD_KEYS[field]);

const getWeightLabel = ({
	mode,
	isMobile,
	weightLabel,
}: Pick<FieldOptions, "mode" | "isMobile" | "weightLabel">) => {
	if (mode === "add") {
		return weightLabel ?? "Total Weight";
	}
	if (isMobile) {
		return "Weight (lbs)";
	}
	return "Total Weight (lb)";
};

const getDimensionLabel = (
	dimension: "length" | "width" | "height",
	options: Pick<FieldOptions, "mode" | "isMobile" | "measuringType">,
) => {
	const labels: Record<typeof dimension, string> = {
		length: "L",
		width: "W",
		height: "H",
	};
	const labelPrefix = labels[dimension];
	if (options.mode === "add") {
		const unit = options.measuringType === "KG/CM" ? "cm" : "in";
		return `${labelPrefix}(${unit})`;
	}
	if (options.isMobile) {
		return `${labelPrefix}(in)`;
	}
	const desktopLabels: Record<typeof dimension, string> = {
		length: "Length (in)",
		width: "Width (in)",
		height: "Height (in)",
	};
	return desktopLabels[dimension];
};

const getFieldStyle = (
	mode: CommodityFormProps["mode"],
	addModeMinWidth: number,
): InputFieldConfig["wrapperStyle"] =>
	mode === "add"
		? getFieldWrapperStyle(addModeMinWidth)
		: { flex: "1 1 120px", minWidth: 0 };

const createDescriptionField = ({
	mode,
	values,
	groupId,
	requireDescription,
}: Pick<
	FieldOptions,
	"mode" | "values" | "groupId" | "requireDescription"
>) => ({
	key: getPresentationFieldKey("description", mode),
	label: "Commodity Description",
	type: "input" as const,
	value: values.description ?? "",
	labelId: mode === "add" ? `description-label-${groupId}` : undefined,
	required: mode === "add" && !!requireDescription,
	wrapperStyle: getFieldStyle(mode, 115),
});

const createDimensionFields = (options: FieldOptions): InputFieldConfig[] => {
	const { values, groupId, mode } = options;
	return (["length", "width", "height"] as const).map((dimension, index) => ({
		key: getPresentationFieldKey(dimension, mode),
		label: getDimensionLabel(dimension, options),
		type: "numeric",
		value: values[dimension],
		labelId: mode === "add" ? `${dimension}-label-${groupId}` : undefined,
		required: mode === "add" || options.isMobile,
		wrapperStyle: getFieldStyle(mode, 50),
		after: getDimensionAfter(mode, index),
	}));
};

const createCoreFields = (options: FieldOptions): InputFieldConfig[] => {
	const { values, groupId, mode, unitTypes, freightClassData } = options;
	const dimensions = createDimensionFields(options);
	const fieldsBeforeDescription: InputFieldConfig[] = [
		{
			key: getPresentationFieldKey("quantity", mode),
			label: "Qty",
			type: "numeric",
			value: values.quantity,
			labelId: mode === "add" ? `quantity-label-${groupId}` : undefined,
			required: mode === "add" || options.isMobile,
			wrapperStyle: getFieldStyle(mode, 40),
		},
		{
			key: getPresentationFieldKey("unit", mode),
			label: "Unit",
			type: "dropdown",
			value: values.unit ?? (mode === "add" ? { id: 0, name: "select" } : null),
			data: unitTypes ?? [],
			labelId: mode === "add" ? `unit-label-${groupId}` : undefined,
			testId: mode === "add" ? "handling-unit-unit-dropdown" : undefined,
			required: false,
			wrapperStyle: getFieldStyle(mode, 75),
		},
		{
			key: getPresentationFieldKey("weight", mode),
			label: getWeightLabel(options),
			type: "numeric",
			value: values.weight,
			labelId: mode === "add" ? `weight-label-${groupId}` : undefined,
			required: mode === "add" || options.isMobile,
			wrapperStyle: getFieldStyle(mode, 70),
		},
		...dimensions,
	];
	const fieldsAfterDescription: InputFieldConfig[] = [
		{
			key: getPresentationFieldKey("class", mode),
			label: "Class",
			type: "dropdown",
			value: values.class ?? (mode === "add" ? { id: 0, name: "" } : null),
			data: freightClassData ?? [],
			labelId: mode === "add" ? `class-label-${groupId}` : undefined,
			testId: mode === "add" ? "handling-unit-class-dropdown" : undefined,
			required:
				mode === "add" ? (options.requireClass ?? true) : options.isMobile,
			textField: "code",
			clearable: true,
			clearLabel: "",
			clearValue: mode === "add" ? { id: 0, name: "", code: "" } : null,
			wrapperStyle: getFieldStyle(mode, 55),
		},
		{
			key: getPresentationFieldKey("nmfc", mode),
			label: "NMFC",
			type: "input",
			value: values.nmfc ?? "",
			labelId: mode === "add" ? `nmfc-label-${groupId}` : undefined,
			required: false,
			pattern: "0123456789-",
			wrapperStyle: getFieldStyle(mode, 58),
		},
	];

	const description = createDescriptionField(options);
	return mode === "add"
		? [...fieldsBeforeDescription, description, ...fieldsAfterDescription]
		: [description, ...fieldsBeforeDescription, ...fieldsAfterDescription];
};

const createSupplementalFields = (
	options: FieldOptions,
): InputFieldConfig[] => {
	const { values, groupId, mode } = options;
	const fields: InputFieldConfig[] = [];
	if (mode === "add") {
		fields.push({
			key: "pieceCount",
			label: "Piece Count",
			type: "numeric",
			className: "piece-case-count-input",
			value:
				values.pieceCount && values.pieceCount > 0 ? values.pieceCount : null,
			labelId: `pieceCount-label-${groupId}`,
			required: options.requirePieceCount ?? true,
			show: options.showPieceCount ?? false,
			wrapperStyle: getFieldWrapperStyle(60),
		});
	}
	const density = formatDecimalDisplay(values.density);
	const estimatedClassValue = getEstClass(values.density ?? 0);
	fields.push(
		{
			key: "density",
			label: "Density",
			type: "display",
			value: density,
			labelId: mode === "add" ? `density-label-${groupId}` : undefined,
			hasToolTip: true,
			tooltip:
				"Density is calculated as weight divided by volume (cubic feet). This helps determine freight classification.",
			required: false,
			show: options.showCalculatedFields ?? false,
			wrapperStyle: getFieldStyle(mode, 55),
		},
		{
			key: "estClass",
			label: "Est Class",
			type: "display",
			value: estimatedClassValue === 0 ? "—" : String(estimatedClassValue),
			labelId: mode === "add" ? `estClass-label-${groupId}` : undefined,
			hasToolTip: true,
			tooltip:
				"The estimated classification is based on density only. Your actual classification will be based on your handling unit's transportation characteristics, including density, stowability, handling and liability. Charges may be incurred if you select the wrong class. If you have questions about your freight’s actual classification, please contact your TQL Account Representative.",
			required: false,
			show: options.showCalculatedFields ?? false,
			wrapperStyle: getFieldStyle(mode, 65),
		},
	);
	if (options.showCommodityFlags) {
		fields.push(
			{
				key: "isStackable",
				label: "Stackable",
				type: "checkbox",
				value: values.isStackable,
				required: false,
				wrapperStyle: getFieldStyle(mode, 120),
			},
			{
				key: "isHazmat",
				label: "Hazmat",
				type: "checkbox",
				value: values.isHazmat,
				required: false,
				wrapperStyle: getFieldStyle(mode, 120),
			},
		);
	}
	return fields;
};

export const CommodityForm = ({
	values,
	mode,
	onChange,
	groupId,
	unitTypes,
	freightClassData,
	isMobile,
	measuringType = "LB/IN",
	weightLabel,
	requireDescription = false,
	requireClass = true,
	showPieceCount = false,
	requirePieceCount = true,
	showCalculatedFields = false,
	showCommodityFlags = false,
	disabled = false,
	validationErrors,
}: CommodityFormProps) => {
	const options: FieldOptions = {
		mode,
		isMobile,
		measuringType,
		weightLabel,
		requireDescription,
		requireClass,
		showPieceCount,
		requirePieceCount,
		showCalculatedFields,
		showCommodityFlags,
		unitTypes,
		freightClassData,
		values,
		groupId,
	};
	const fieldDefinitions = [
		...createCoreFields(options),
		...createSupplementalFields(options),
	];
	const fields = fieldDefinitions.map((field) => ({
		...field,
		disabled: disabled || field.disabled,
	}));
	const fieldKeyMap =
		mode === "add" ? ADD_FORM_FIELDS_BY_KEY : EDIT_FORM_FIELDS_BY_KEY;
	const handleChange = (
		field: keyof CommodityFormValues,
		value: InputGroupValue,
	) => {
		const formField = fieldKeyMap[String(field)];
		if (formField) {
			onChange(formField, value);
		}
	};

	return (
		<InputFieldsGroup<CommodityFormValues>
			fields={fields}
			groupId={groupId}
			onChange={handleChange}
			validationErrors={validationErrors}
		/>
	);
};
